/*
 * @providesModule SkillzButton
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  TouchableOpacityProps,
  StyleProp,
  ViewStyle,
  ActivityIndicatorProps,
  TextStyle,
} from 'react-native';
import SkillzImage from 'SkillzImage';

import styles, {DEFAULT_PRIMARY_BUTTON_COLOR, getDisabledColorFrom} from './SkillzButton.style';

const defaultLoadingProps = (
  type: 'solid' | 'clear' | 'outline',
  colors: ButtonColors | undefined
): ActivityIndicatorProps => ({
  color:
    type === 'solid'
      ? colors?.secondary ?? 'white'
      : colors?.primary ?? DEFAULT_PRIMARY_BUTTON_COLOR,
  size: 'small',
});

type ButtonColors = {
  primary: string;
  secondary?: string;
  disabled?: string;
};

export type ButtonProps = TouchableOpacityProps & {
  title?: string | React.ReactElement<{}>;
  titleStyle?: StyleProp<TextStyle>;
  buttonStyle?: StyleProp<ViewStyle>;
  type?: 'solid' | 'clear' | 'outline';
  loading?: boolean;
  loadingStyle?: StyleProp<ViewStyle>;
  loadingProps?: ActivityIndicatorProps;
  containerStyle?: StyleProp<ViewStyle>;
  iconSource?: string;
  iconContainerStyle?: StyleProp<ViewStyle>;
  iconPosition?: 'left' | 'right' | 'top' | 'bottom';
  linearGradientProps?: object;
  ViewComponent?: typeof React.Component;
  disabled?: boolean;
  disabledStyle?: StyleProp<ViewStyle>;
  disabledTitleStyle?: StyleProp<TextStyle>;
  raised?: boolean;
  colors?: ButtonColors;
  onLayout?: () => void;
  innerRef?: any;
  onPressDelay?: number;
};

const SkillzButton = ({
  containerStyle,
  // eslint-disable-next-line no-console
  onPress = () => console.log('Please attach a method to this component'),
  buttonStyle,
  type = 'solid',
  loading = false,
  loadingStyle,
  loadingProps: passedLoadingProps,
  title = '',
  titleStyle: passedTitleStyle,
  iconSource,
  iconContainerStyle,
  disabled = false,
  disabledStyle,
  disabledTitleStyle,
  raised = false,
  linearGradientProps,
  ViewComponent = View,
  iconPosition = 'left',
  colors = {primary: DEFAULT_PRIMARY_BUTTON_COLOR},
  onLayout = () => {},
  innerRef = null,
  onPressDelay = 0,
  ...attributes
}: ButtonProps) => {
  const [pressDelayed, setPressDelayed] = useState(false);
  const delayTimeout = useRef(null);
  const appliedStyles = containerStyle ? StyleSheet.flatten(containerStyle) : undefined;

  useEffect(() => {
    return () => {
      if (delayTimeout.current) {
        clearTimeout(delayTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (linearGradientProps && !ViewComponent) {
      console.error(
        "You need to pass a ViewComponent to use linearGradientProps !\nExample: ViewComponent={require('react-native-linear-gradient')}"
      );
    }
  });

  const handleOnPress = useCallback(
    (evt) => {
      if (!loading && !pressDelayed) {
        setPressDelayed(true);
        delayTimeout.current = setTimeout(() => {
          setPressDelayed(false);
          delayTimeout.current = null;
        }, onPressDelay);
        onPress(evt);
      }
    },
    [loading, onPress, pressDelayed]
  );

  const titleStyle: StyleProp<TextStyle> = StyleSheet.flatten([
    {
      color: type === 'solid' ? colors?.secondary ?? 'white' : colors.primary,
    },
    styles.title,
    passedTitleStyle,
    disabled && disabledTitleStyle,
  ]);

  const loadingProps: ActivityIndicatorProps = {
    ...defaultLoadingProps(type, colors),
    ...passedLoadingProps,
  };

  const getContainerFlexDirection = () => {
    switch (iconPosition) {
      case 'top':
        return 'column';
      case 'bottom':
        return 'column-reverse';
      case 'right':
        return 'row-reverse';
      case 'left':
      default:
        return 'row';
    }
  };

  const getViewComponentStyle = () => {
    const backgroundColor = appliedStyles?.backgroundColor
      ? appliedStyles.backgroundColor
      : type === 'solid'
        ? colors.primary
        : type === 'outline'
          ? colors.secondary || 'transparent'
          : 'transparent';

    return StyleSheet.flatten([
      styles.button,
      styles.buttonOrientation,
      {
        flexDirection: getContainerFlexDirection(),
      },
      {
        backgroundColor,
        borderColor: colors?.primary,
        borderWidth: type === 'outline' ? StyleSheet.hairlineWidth * 2 : 0,
      },
      buttonStyle,
      disabled &&
        type === 'solid' && {
          backgroundColor: colors?.disabled ?? getDisabledColorFrom(colors.primary),
        },
      disabled &&
        type === 'outline' && {
          borderColor: colors?.disabled ?? getDisabledColorFrom(colors.primary),
        },
      disabled && disabledStyle,
    ]);
  };

  return (
    <View
      style={[
        styles.container,
        appliedStyles,
        raised && !disabled && type !== 'clear' && styles.raised,
      ]}
    >
      <TouchableOpacity
        onPress={handleOnPress}
        delayPressIn={0}
        activeOpacity={0.3}
        accessibilityRole='button'
        disabled={disabled}
        {...attributes}
      >
        <ViewComponent
          {...linearGradientProps}
          style={getViewComponentStyle()}
          ref={innerRef}
          onLayout={onLayout}
        >
          {loading && (
            <ActivityIndicator
              style={StyleSheet.flatten([styles.loading, loadingStyle])}
              color={loadingProps.color}
              size={loadingProps.size}
              {...loadingProps}
            />
          )}

          {!loading && !!iconSource && (
            <View style={iconContainerStyle}>
              <SkillzImage style={styles.iconContainer} source={iconSource} />
            </View>
          )}

          {!loading && !!title && <Text allowFontScaling={false} style={titleStyle}>{title}</Text>}
        </ViewComponent>
      </TouchableOpacity>
    </View>
  );
};

export default SkillzButton;
